import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Trophy, TrendingUp, TrendingDown, ShieldCheck, Sparkles, AlertTriangle,
  ArrowUpRight, Link2, Award, ArrowUp, ArrowDown, Minus, Download, Loader2, Check, Bell,
} from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/layout/TopBar";
import useApi from "../hooks/useApi";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";

const PALETTE = ["#2F6BFF", "#6FA015", "#B8892B", "#D0526B", "#7A5AF8", "#0EA5A5", "#E0691A"];

function initials(name) {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}
function statusColor(score) {
  if (score >= 80) return "#5F8213";
  if (score >= 70) return "#7CA015";
  if (score >= 55) return "#B8892B";
  return "#D0526B";
}
function TrendCell({ v }) {
  if (v > 0) return <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: "#5F8213" }}><ArrowUp size={13} strokeWidth={2.5} />+{v}</span>;
  if (v < 0) return <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: "#D0526B" }}><ArrowDown size={13} strokeWidth={2.5} />{v}</span>;
  return <span className="inline-flex items-center gap-0.5 text-ink-3"><Minus size={13} />0</span>;
}

function Sparkline({ series }) {
  if (!series || series.length < 2) return null;
  const w = 240, h = 56, pad = 4;
  const vals = series.map((s) => s.score);
  const min = Math.min(...vals) - 2, max = Math.max(...vals) + 2;
  const pts = series.map((s, i) => {
    const x = pad + (i / (series.length - 1)) * (w - pad * 2);
    const y = h - pad - ((s.score - min) / (max - min || 1)) * (h - pad * 2);
    return [x, y];
  });
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${d} L${pts[pts.length - 1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`;
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="mt-1">
      <defs><linearGradient id="gspark" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#93C020" stopOpacity="0.35" /><stop offset="100%" stopColor="#93C020" stopOpacity="0" /></linearGradient></defs>
      <path d={area} fill="url(#gspark)" />
      <path d={d} fill="none" stroke="#5F8213" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const SEV = {
  high: { color: "#C0455F", bg: "rgba(192,69,95,.1)", label: "High" },
  medium: { color: "#A9781F", bg: "rgba(169,120,31,.12)", label: "Medium" },
  low: { color: "#6B6E62", bg: "rgba(107,110,98,.1)", label: "Low" },
};

export default function GroupDashboard() {
  const navigate = useNavigate();
  const { setBranchId } = useBranch();
  const { setViewAs } = useAuth();
  const { data } = useApi("/group");
  const { data: alertData } = useApi("/group/alerts");
  const [downloading, setDownloading] = React.useState(false);
  const branches = data?.branches || [];
  const alerts = alertData?.alerts || [];

  const drill = (id) => { setBranchId(id); setViewAs("branch"); navigate("/app"); };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/group/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "group-growth-report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Monthly group report downloaded");
    } catch (e) {
      toast.error("Could not generate the report");
    } finally { setDownloading(false); }
  };

  const cards = [
    { l: "Group average", v: data?.average, icon: Trophy, c: "#5F8213" },
    { l: "Top performer", v: data?.best, icon: Award, c: "#2F6BFF", small: true },
    { l: "Fastest growing", v: data?.fastest_growing, icon: TrendingUp, c: "#7CA015", small: true },
    { l: "Needs attention", v: data?.needs_attention, icon: AlertTriangle, c: "#D0526B", small: true },
  ];

  return (
    <>
      <TopBar title="Group overview" subtitle={data ? `${data.org_name} · ${data.branch_count} branches` : "Loading…"} />
      <div className="p-6 sm:p-8 max-w-[1400px] space-y-6">

        {/* Report toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-ink-2">
            <Bell size={15} strokeWidth={1.75} style={{ color: alerts.length ? "#C0455F" : "#5F8213" }} />
            {alerts.length ? `${alerts.length} branch alert${alerts.length > 1 ? "s" : ""} this month` : "All branches healthy"}
          </div>
          <button onClick={downloadReport} disabled={downloading} className="btn-primary focus-ring" data-testid="download-group-report">
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} strokeWidth={2} />} Download monthly report
          </button>
        </div>

        {/* No-relink authorisation banner */}
        <div className="card p-5 flex items-start gap-4" style={{ background: "var(--grad-green-soft)" }} data-testid="group-relink-banner">
          <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0" style={{ background: "#fff" }}><Link2 size={18} color="#5F8213" strokeWidth={1.75} /></span>
          <div>
            <div className="font-bold text-ink">Group access — every branch, no re-linking</div>
            <p className="text-sm text-ink-2 mt-0.5">You're authorised at group level, so all {data?.branch_count || ""} branches' social stats flow straight into this view. Branch managers keep their own logins — nothing needs reconnecting.</p>
          </div>
        </div>

        {/* Branch alerts */}
        <div data-testid="group-alerts">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={17} color="#C0455F" strokeWidth={1.75} />
            <h2 className="text-lg font-bold tracking-tight text-ink">Needs your attention</h2>
            {alerts.length > 0 && <span className="text-xs font-bold rounded-full px-2 py-0.5" style={{ background: "rgba(192,69,95,.1)", color: "#C0455F" }}>{alerts.length}</span>}
          </div>
          {alerts.length === 0 ? (
            <div className="card p-6 flex items-center gap-3" data-testid="group-alerts-empty">
              <span className="grid place-items-center w-10 h-10 rounded-full" style={{ background: "var(--grad-green-soft)" }}><Check size={18} color="#5F8213" strokeWidth={2.5} /></span>
              <div><div className="font-semibold text-ink">Every branch is on track</div><div className="text-sm text-ink-2">No branch has dropped or stalled this month.</div></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {alerts.map((a) => {
                const s = SEV[a.severity] || SEV.low;
                return (
                  <div key={a.id} className="card card-hover p-4 flex gap-3" data-testid={`alert-${a.id}`}>
                    <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0 mt-0.5" style={{ background: s.bg }}><AlertTriangle size={16} strokeWidth={1.75} style={{ color: s.color }} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ color: s.color, background: s.bg }}>{s.label}</span>
                        <span className="font-semibold text-ink text-sm">{a.branch_name} — {a.title}</span>
                      </div>
                      <p className="text-sm text-ink-2 mt-1">{a.detail}</p>
                      <p className="text-xs mt-1.5" style={{ color: "#5F8213" }}>Recommended: {a.recommendation}</p>
                    </div>
                    <button onClick={() => drill(a.branch_id)} className="btn-secondary focus-ring text-xs self-start" data-testid={`alert-view-${a.branch_id}`}>View <ArrowUpRight size={13} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <motion.div key={c.l} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="card p-5" data-testid={`group-stat-${c.l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <c.icon size={16} strokeWidth={1.75} style={{ color: c.c }} />
              <div className={`font-extrabold tracking-tighter text-ink mt-2 ${c.small ? "text-2xl" : "text-4xl"}`}>{c.v ?? "—"}</div>
              <div className="text-xs text-ink-2 mt-0.5">{c.l}</div>
            </motion.div>
          ))}
        </div>

        {/* Group trend */}
        <div className="card p-5" data-testid="group-trend">
          <div className="flex items-center justify-between">
            <div><span className="label-caps">How the group is tracking</span><div className="text-sm text-ink-2 mt-0.5">Average Growth Score across all branches · 12 months</div></div>
            <div className="text-right"><div className="text-3xl font-extrabold tracking-tighter text-ink">{data?.average ?? "—"}</div><div className="text-xs" style={{ color: "#5F8213" }}>{data?.improving ?? 0} of {data?.branch_count ?? 0} improving</div></div>
          </div>
          <Sparkline series={data?.trend_series} />
        </div>

        {/* League table */}
        <div>
          <div className="flex items-center gap-2 mb-3"><Trophy size={17} color="#5F8213" strokeWidth={1.75} /><h2 className="text-lg font-bold tracking-tight text-ink">Branch league table</h2></div>
          <div className="card overflow-hidden" data-testid="group-league">
            <div className="hidden lg:grid grid-cols-12 px-5 py-2.5 label-caps border-b" style={{ borderColor: "#E7E8E4" }}>
              <span className="col-span-1">#</span><span className="col-span-3">Branch</span><span className="col-span-2">Growth Score</span><span className="col-span-1">Trend</span><span className="col-span-2">Local rank</span><span className="col-span-3">Strongest / weakest</span>
            </div>
            <div className="divide-y" style={{ borderColor: "#E7E8E4" }}>
              {branches.map((b, i) => (
                <button key={b.id} onClick={() => drill(b.id)} data-testid={`league-row-${b.id}`}
                  className="w-full grid grid-cols-12 items-center gap-2 px-5 py-4 text-left hover:bg-[#F6F8F1] transition-colors group">
                  <span className="col-span-2 lg:col-span-1 text-lg font-extrabold tracking-tighter" style={{ color: i === 0 ? "#5F8213" : "#B8BAAF" }}>{b.group_rank}</span>
                  <div className="col-span-10 lg:col-span-3 flex items-center gap-3 min-w-0">
                    <span className="grid place-items-center w-9 h-9 rounded-lg text-white text-xs font-bold shrink-0" style={{ background: PALETTE[i % PALETTE.length] }}>{initials(b.name)}</span>
                    <div className="min-w-0"><div className="font-semibold text-ink truncate">{b.name}</div>{b.primary && <div className="text-[11px] text-ink-3">Your branch</div>}</div>
                  </div>
                  <div className="col-span-4 lg:col-span-2 flex items-center gap-2">
                    <span className="text-2xl font-extrabold tracking-tighter text-ink">{b.score}</span>
                    <span className="text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ color: statusColor(b.score), background: `${statusColor(b.score)}1f` }}>{b.status}</span>
                  </div>
                  <div className="col-span-3 lg:col-span-1 text-sm"><TrendCell v={b.trend} /></div>
                  <div className="col-span-5 lg:col-span-2 text-sm text-ink-2">#{b.rank} of {b.field_size} <span className="text-ink-3">· better than {b.better_than}%</span></div>
                  <div className="col-span-11 lg:col-span-2 text-xs">
                    {b.top_component && <div className="flex items-center gap-1" style={{ color: "#5F8213" }}><TrendingUp size={12} /> {b.top_component.label} <span className="text-ink-3">{b.top_component.score}</span></div>}
                    {b.weak_component && <div className="flex items-center gap-1 mt-0.5" style={{ color: "#B8892B" }}><TrendingDown size={12} /> {b.weak_component.label} <span className="text-ink-3">{b.weak_component.score}</span></div>}
                  </div>
                  <div className="hidden lg:flex col-span-1 justify-end"><ArrowUpRight size={16} className="text-ink-3 group-hover:text-ink group-hover:translate-x-0.5 transition-all" /></div>
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-ink-3 mt-3 flex items-center gap-1.5"><ShieldCheck size={13} /> Read-only oversight — click any branch to drill into its full Growth dashboard.</p>
        </div>
      </div>
    </>
  );
}
